/**
 * TransferProgress Component
 *
 * Displays progress for file transfers during bootloader and firmware operations.
 * Shows a progress bar with percentage and status message.
 */

import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from '../../../shared/Icon';

/**
 * Props for TransferProgress
 */
export interface TransferProgressProps {
  /** Progress percentage (0-100) */
  progress: number;
  /** Status message to display */
  message?: string;
  /** Whether the operation is currently active */
  isActive?: boolean;
  /** Whether the operation completed successfully */
  isComplete?: boolean;
  /** Whether the operation failed */
  isError?: boolean;
}

/**
 * TransferProgress component
 */
export const TransferProgress: React.FC<TransferProgressProps> = ({
  progress,
  message = '',
  isActive = false,
  isComplete = false,
  isError = false,
}) => {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <ProgressContainer>
      <ProgressHeader>
        <ProgressLabel>
          {isComplete && <Icon name="check_circle" color="#66bb6a" size="small" />}
          {isError && (
            <Icon name="error" color="var(--color-react-select-danger)" size="small" />
          )}
          {isActive && !isComplete && !isError && (
            <SpinningIcon>
              <Icon name="sync" color="var(--color-primary)" size="small" />
            </SpinningIcon>
          )}
          <span>{message || 'Processing...'}</span>
        </ProgressLabel>
      </ProgressHeader>

      <ProgressBarContainer>
        <ProgressBarTrack>
          <ProgressBarFill
            $progress={clampedProgress}
            $isActive={isActive}
            $isComplete={isComplete}
            $isError={isError}
          />
        </ProgressBarTrack>
      </ProgressBarContainer>
    </ProgressContainer>
  );
};

// Animations

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

// Styled Components

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  background: var(--color-surface-1);
  border-radius: 10px;
  margin: 16px 0;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ProgressLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--color-text);
`;

const SpinningIcon = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: var(--color-surface-2);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{
  $progress: number;
  $isActive: boolean;
  $isComplete: boolean;
  $isError: boolean;
}>`
  width: ${(props) => props.$progress}%;
  height: 100%;
  background: ${(props) => {
    if (props.$isError) return 'var(--color-react-select-danger)';
    if (props.$isComplete) return '#66bb6a';
    return 'var(--color-primary)';
  }};
  border-radius: 4px;
  transition: width 300ms ease-out;
  animation: ${(props) => (props.$isActive && !props.$isComplete ? pulse : 'none')} 1.5s
    ease-in-out infinite;
`;

export default TransferProgress;
